/** @format */

import React, { useMemo, useState } from "react";
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Button,
  Chip,
  CircularProgress,
  IconButton,
  Paper,
  Stack,
  Tooltip,
  Typography,
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import EditIcon from "@mui/icons-material/Edit";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import PictureAsPdfIcon from "@mui/icons-material/PictureAsPdf";
import InsertPhotoIcon from "@mui/icons-material/InsertPhoto";
import TableChartIcon from "@mui/icons-material/TableChart";
import InsertDriveFileIcon from "@mui/icons-material/InsertDriveFile";
import FolderIcon from "@mui/icons-material/Folder";
import CloudUploadIcon from "@mui/icons-material/CloudUpload";

const isFileValue = (v) => typeof v === "string" && /^https?:\/\//i.test(v);

const IMAGE_EXTENSIONS = ["jpg", "jpeg", "png", "gif", "webp", "bmp", "svg"];
const EXCEL_EXTENSIONS = ["xls", "xlsx", "xlsm", "csv"];

// Strip query/hash before reading the extension so signed URLs (?token=..) still match.
const getFileExtension = (v) => v.split(/[#?]/)[0].split(".").pop().toLowerCase();

// Classifies a file URL by extension so the right icon/preview renders — defaults to a
// generic file icon for anything not explicitly recognised (word, ppt, etc.), rather than
// assuming PDF.
const getFileKind = (v) => {
  const ext = getFileExtension(v);
  if (IMAGE_EXTENSIONS.includes(ext)) return "image";
  if (EXCEL_EXTENSIONS.includes(ext)) return "excel";
  if (ext === "pdf") return "pdf";
  return "file";
};

// Friendly labels for the sub-fields of a REPEATABLE input value, keyed by the parent
// field key. Used when expanding an array value into one row per entry sub-field.
const REPEATABLE_ITEM_LABELS = {
  registerAgreements: { file: "Registered Agreement", startDate: "Start Date", expiryDate: "Expiry Date" },
};

// Format a raw stepInputs value for display: booleans → Yes/No; everything else as-is.
const formatDisplayValue = (v) => {
  if (typeof v === "boolean") return v ? "Yes" : "No";
  return v;
};

// A single field line inside a DocumentCard: label on the left, value/actions on the right.
// File values show a "View" link (and a "Replace" action when the key is replaceable); every
// other value is rendered as text.
const DocumentRow = ({ row, onReplace, replacing = false }) => {
  const isFile = isFileValue(row.value);
  const fileKind = isFile ? getFileKind(row.value) : null;
  const [imageErrored, setImageErrored] = useState(false);
  return (
    <Stack direction="row" alignItems="center" justifyContent="space-between" gap={1}>
      <Typography
        variant="body2"
        color="text.secondary"
        noWrap
        title={row.label}
        sx={{ flex: 1, minWidth: 0 }}
      >
        {row.label}
      </Typography>
      <Stack
        direction="row"
        alignItems="center"
        spacing={0.5}
        sx={{ flexShrink: 0, maxWidth: "60%", justifyContent: "flex-end" }}
      >
        {isFile ? (
          <>
            {fileKind === "image" && !imageErrored ? (
              <Box
                component="img"
                src={row.value}
                alt={row.label}
                onError={() => setImageErrored(true)}
                sx={{ width: 24, height: 24, borderRadius: 0.5, objectFit: "cover", flexShrink: 0 }}
              />
            ) : fileKind === "image" ? (
              <InsertPhotoIcon sx={{ color: "#1a73e8", fontSize: 18 }} />
            ) : fileKind === "excel" ? (
              <TableChartIcon sx={{ color: "#107c41", fontSize: 18 }} />
            ) : fileKind === "pdf" ? (
              <PictureAsPdfIcon sx={{ color: "#d93025", fontSize: 18 }} />
            ) : (
              <InsertDriveFileIcon sx={{ color: "#5f6368", fontSize: 18 }} />
            )}
            <IconButton
              size="small"
              href={row.value}
              target="_blank"
              rel="noopener noreferrer"
              title="View"
            >
              <OpenInNewIcon fontSize="small" />
            </IconButton>
          </>
        ) : (
          <Typography
            variant="body2"
            sx={{ fontWeight: 600, textAlign: "right", overflowWrap: "anywhere" }}
            title={String(row.value ?? "")}
          >
            {row.value ?? "—"}
          </Typography>
        )}
        {onReplace && (
          <IconButton
            size="small"
            onClick={onReplace}
            title="Replace file"
            disabled={replacing}
          >
            {replacing ? <CircularProgress size={16} /> : <CloudUploadIcon fontSize="small" />}
          </IconButton>
        )}
      </Stack>
    </Stack>
  );
};

// Read-only card for a single document type (e.g. FSSAI, Signed Agreement). Shows the document
// title once, then every captured field for that document (status, file, dates, metadata) as
// its own row — replacing the old one-card-per-field layout.
const DocumentCard = ({
  title,
  rows,
  replaceableFields = {},
  onReplaceField,
  replacingFieldKey = null,
}) => (
  <Paper
    variant="outlined"
    sx={{ p: 1.5, display: "flex", flexDirection: "column", gap: 1, alignSelf: "start" }}
  >
    <Stack direction="row" alignItems="center" gap={1}>
      <Box
        sx={{
          width: 32,
          height: 32,
          borderRadius: 1,
          backgroundColor: "#00893814",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          flexShrink: 0,
        }}
      >
        <FolderIcon sx={{ color: "#008938", fontSize: 20 }} />
      </Box>
      <Typography variant="subtitle2" sx={{ fontWeight: 700 }} noWrap title={title}>
        {title}
      </Typography>
    </Stack>
    <Box
      sx={{
        display: "grid",
        gridTemplateColumns: {
          xs: "minmax(0, 1fr)",
          sm: "repeat(2, minmax(0, 1fr))",
        },
        columnGap: 3,
        rowGap: 0.75,
      }}
    >
      {rows.map((row) => (
        <DocumentRow
          key={row.id}
          row={row}
          onReplace={
            onReplaceField && replaceableFields[row.id]
              ? () => onReplaceField(row.id)
              : null
          }
          replacing={replacingFieldKey === row.id}
        />
      ))}
    </Box>
  </Paper>
);

/**
 * Configurable grouping of submitted step inputs into user-facing sections.
 * Each entry renders as one accordion. Within it, every `documents[]` entry
 * renders as ONE card for that document type, listing all of its `keys` (status,
 * file, dates, metadata) as rows — regardless of which step captured them.
 * Add/rename sections, documents, or move keys here — nothing else in the
 * component is group-aware.
 */
export const DEFAULT_INPUT_GROUPS = [
  {
    key: "loiLease",
    title: "Agreement",
    documents: [
      {
        key: "loiForSigning",
        title: "LOI for Signing",
        keys: ["loiApplicability", "loiForSigning"],
      },
      {
        key: "executedLoi",
        title: "Executed LOI",
        keys: ["executedLoi", "executedLoiDocumentDate"],
      },
      {
        key: "ddr",
        title: "Title Diligence Report (DDR)",
        keys: ["ddr", "ddrDocumentDate"],
      },
      {
        key: "signedAgreement",
        title: "Signed Agreement",
        keys: [
          "signedAgreement",
          "leaseStartDate",
          "leaseExpiryDate",
          "monthlyRentMG",
          "revShareDineInPct",
          "revShareDeliveryPct",
          "landlordNameGstPan",
          "leaseAddress",
          "fitOutPeriodDays",
          "lockInPeriodMonths",
          "monthlyCam",
          "investmentFromLandlord",
          "rSecDepositRs",
          "rSecDepositMonths",
          "rentRevShareExclAggregator",
        ],
      },
      {
        key: "registerAgreement",
        title: "Registered Agreement",
        // `registerAgreements` is a REPEATABLE list (one entry per shop). Legacy scalar
        // keys are kept so pre-change records still render.
        keys: [
          "registerAgreement",
          "registerAgreements",
          "registerAgreementFile",
          "registerAgreementStartDate",
          "registerAgreementExpiryDate",
        ],
      },
      {
        key: "possessionLetter",
        title: "Possession Letter",
        keys: ["possessionLetter", "possessionLetterDocumentDate"],
      },
    ],
  },
  {
    key: "licences",
    title: "Licences",
    documents: [
      {
        key: "licenseFeasibility",
        title: "License Feasibility",
        keys: ["licenseFeasibility"],
      },
      {
        key: "fssai",
        title: "FSSAI",
        keys: ["fssai", "fssaiFile", "fssaiStartDate", "fssaiExpiryDate"],
      },
      {
        key: "shopAct",
        title: "S&E",
        keys: ["shopAct", "shopActFile", "shopActStartDate", "shopActExpiryDate"],
      },
      {
        key: "trade",
        title: "Trade",
        keys: ["trade", "tradeFile", "tradeStartDate", "tradeExpiryDate"],
      },
      {
        key: "health",
        title: "Health",
        keys: [
          "health",
          "healthFile",
          "healthLicenseName",
          "healthStartDate",
          "healthExpiryDate",
        ],
      },
      {
        key: "fireNoc",
        title: "Fire NOC",
        keys: ["fireNoc", "fireNocFile", "fireNocStartDate", "fireNocExpiryDate"],
      },
      {
        key: "signage",
        title: "Signage",
        keys: ["signage", "signageFile", "signageStartDate", "signageExpiryDate"],
      },
      {
        key: "cte",
        title: "CTE",
        keys: ["cte", "cteFile", "cteStartDate", "cteExpiryDate"],
      },
      {
        key: "cto",
        title: "CTO",
        keys: ["cto", "ctoFile", "ctoStartDate", "ctoExpiryDate"],
      },
      {
        key: "policeNoc",
        title: "Police NOC",
        keys: ["policeNoc", "policeNocFile", "policeNocStartDate", "policeNocExpiryDate"],
      },
      {
        key: "otla",
        title: "Otla Permission",
        keys: ["otla", "otlaFile", "otlaStartDate", "otlaExpiryDate"],
      },
    ],
  },
  {
    key: "storeOpening",
    title: "Store Opening",
    documents: [
      { key: "nsoChecklist", title: "NSO Checklist", keys: ["nsoChecklist"] },
      { key: "snagList", title: "Snag List", keys: ["snagListStatus", "snagList"] },
    ],
  },
];


/**
 * Overrides the auto-derived field label with a more user-readable name.
 * Keyed by the input field key. Falls back to the step-master label, then the
 * raw key, when a key is absent here.
 */
export const DEFAULT_FIELD_NAME_MAP = {
  // e.g. loiFile: "Letter of Intent",
};

/**
 * In-place file re-upload config, keyed by the form's CURRENT step. When the form is on
 * one of these steps and the user is that step's approver (or admin), the listed files get
 * a "Replace" action on their Submitted-Inputs card. Clicking it swaps ONLY that file —
 * no step movement, no other data changes (PUT /:id/replace-file).
 *
 *   key          – the input field key shown on the card
 *   storedAtStep – the step under which the file's value actually lives in stepInputs
 *   accept       – allowed upload MIME type(s)
 *
 * Extend this map to allow replacing more files at more steps, e.g.
 *   16: [{ key: "ddr", storedAtStep: 16, accept: "application/pdf" }],
 */
export const REPLACEABLE_FILES_BY_STEP = {
  // Legal (step 13) may replace the LOI that was uploaded back at step 12.
  13: [{ key: "loiForSigning", storedAtStep: 12, accept: "application/pdf" }],
  // Possession Approval (step 17) may replace the docs uploaded at step 16 (Possession Approval Request).
  17: [
    { key: "ddr", storedAtStep: 16, accept: "application/pdf" },
    { key: "licenseFeasibility", storedAtStep: 16, accept: "application/pdf" },
    { key: "signedAgreement", storedAtStep: 16, accept: "application/pdf" },
  ],
};

/**
 * Role-based in-place file replacement, available at ANY stage once the file has been submitted
 * (independent of the form's current step and of the storing step's approver). Keyed by user role;
 * same shape as REPLACEABLE_FILES_BY_STEP entries. Goes through the same PUT /:id/replace-file.
 */
export const ALWAYS_REPLACEABLE_FILES_BY_ROLE = {
  // Licence team may replace the Signed Agreement (uploaded at step 16) at any stage.
  LICENCE_TEAM: [{ key: "signedAgreement", storedAtStep: 16, accept: "application/pdf" }],
};

/**
 * Whether a step's per-key `editableFilesObject` permits editing/replacing the given input key
 * AFTER submission. When the step carries a lock map, ONLY keys explicitly set to `true` are
 * editable — a key absent from the map (or set to `false`) is treated as locked. When there is
 * no map at all, everything stays editable (back-compat with steps that don't define one, e.g.
 * the LOI replace path where the storing step has no lock map).
 */
export const isKeyUploadAllowed = (editableFilesObject, key) => {
  if (editableFilesObject && typeof editableFilesObject === "object") {
    return editableFilesObject[key] === true;
  }
  return true;
};

/**
 * Mirror of the backend `isStepUploadEnabled`: whether a step is upload-enabled at all, which
 * gates editing the shared `attachments` block. Driven by the per-key `editableFilesObject` lock
 * map — a step is upload-enabled when it carries at least one editable file key. In the seeded
 * config this is steps 1–7 (site docs editable); 8–11 have none (final finance/exec sign-off —
 * documents frozen).
 */
export const isStepUploadEnabled = (editableFilesObject) =>
  !!editableFilesObject && Object.values(editableFilesObject).some((v) => v === true);

/**
 * Read-only "Submitted Inputs" section.
 * Renders the dynamic inputs captured during approvals, organised into the
 * configurable groups in `groupsConfig` (one accordion per group). Each group
 * collects inputs by field key, flattened across whichever step captured them.
 * Only shown once the form has progressed past `minStep` (default 12).
 * When a group's underlying step is listed in `editableStepNumbers`, the group
 * header shows an "Edit" button that calls `onEditStep(stepNo)` so the parent
 * can open the edit/re-upload dialog.
 */
const CfV2SubmittedInputs = ({
  stepInputs,
  inputFieldLabelMap = {},
  stepNameMap = {},
  currentStep = 0,
  minStep = 12,
  defaultExpanded = false,
  editableStepNumbers = [],
  onEditStep,
  // Field keys (object/Set-like) the current user may replace in place, the handler invoked
  // with the field key on a card "Replace" click, and the key currently uploading (spinner).
  replaceableFields = {},
  onReplaceField,
  replacingFieldKey = null,
  groupsConfig = DEFAULT_INPUT_GROUPS,
  fieldNameMap = DEFAULT_FIELD_NAME_MAP,
  // Step 21 snag-list points-closed follow-up: current state, whether this user may close
  // points, and the handler invoked (with no args) when they click "Mark Points Closed".
  pointsClosed = null,
  canMarkPointsClosed = false,
  onMarkPointsClosed,
}) => {
  const editable = new Set((editableStepNumbers || []).map(Number));
  const snagListStatus = stepInputs?.["21"]?.snagListStatus;

  // Flatten every step's captured inputs into a single fieldKey -> { value, label, stepNo }
  // lookup, so groups can be assembled by field key regardless of source step.
  const flatInputs = useMemo(() => {
    const map = {};
    const inputs = stepInputs || {};
    Object.keys(inputs).forEach((stepNo) => {
      const entries = inputs[stepNo] || {};
      const labels = inputFieldLabelMap[stepNo] || inputFieldLabelMap[String(stepNo)] || {};
      Object.keys(entries).forEach((fieldKey) => {
        map[fieldKey] = {
          stepNo: Number(stepNo),
          label: fieldNameMap[fieldKey] || labels[fieldKey] || fieldKey,
          value: entries[fieldKey],
        };
      });
    });
    return map;
  }, [stepInputs, inputFieldLabelMap, fieldNameMap]);

  // Turn a document's configured keys into displayable rows. A `<x>File` key is the
  // uploaded companion of status field `<x>`; when both are configured, they merge into
  // one row (prefer the uploaded file, which shows a View link; fall back to status text).
  const buildRows = (keys = []) => {
    const mergedFileKeys = new Set(
      keys.filter((k) => k.endsWith("File") && keys.includes(k.slice(0, -4)))
    );
    return keys
      .filter((k) => !mergedFileKeys.has(k))
      .flatMap((k) => {
        const fileEntry = flatInputs[`${k}File`];
        const statusEntry = flatInputs[k];
        const entry = fileEntry && fileEntry.value ? fileEntry : statusEntry;
        if (!entry || entry.value == null || entry.value === "") return [];

        // REPEATABLE value: expand the array into one row per sub-field per entry.
        if (Array.isArray(entry.value)) {
          const itemLabels = REPEATABLE_ITEM_LABELS[k] || {};
          return entry.value.flatMap((item, i) =>
            Object.keys(item || {})
              .filter((subKey) => item[subKey] != null && item[subKey] !== "")
              .map((subKey) => ({
                id: `${k}.${i}.${subKey}`,
                label: `${itemLabels[subKey] || subKey} #${i + 1}`,
                value: formatDisplayValue(item[subKey]),
              }))
          );
        }

        return [{ id: k, label: statusEntry?.label || entry.label, value: formatDisplayValue(entry.value) }];
      });
  };

  const groups = useMemo(() => {
    return (groupsConfig || [])
      .map((group) => {
        // Each document becomes one card; drop documents with no captured values.
        const documents = (group.documents || [])
          .map((doc) => ({
            key: doc.key || doc.title,
            title: doc.title,
            rows: buildRows(doc.keys),
          }))
          .filter((d) => d.rows.length);

        // Source steps behind this group's keys that the user may edit (drives header buttons).
        const allKeys = (group.documents || []).flatMap((d) => d.keys || []);
        return {
          key: group.key || group.title,
          title: group.title,
          documents,
          editableSteps: [
            ...new Set(
              allKeys.map((k) => flatInputs[k]?.stepNo).filter((s) => s != null && editable.has(s))
            ),
          ],
        };
      })
      .filter((g) => g.documents.length);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [flatInputs, groupsConfig, editableStepNumbers]);

  if (currentStep <= minStep || !groups.length) return null;

  return (
    <>
      {groups.map((group) => (
        <Accordion
          key={group.key}
          defaultExpanded={defaultExpanded}
          disableGutters
          elevation={0}
          sx={{ mt: 2, "&:before": { display: "none" } }}
        >
          <AccordionSummary expandIcon={<ExpandMoreIcon />} sx={{ px: 0 }}>
            <Stack
              direction="row"
              alignItems="center"
              justifyContent="space-between"
              sx={{ width: "100%", pr: 1 }}
            >
              <Typography
                variant="subtitle2"
                sx={{ color: "#008938", fontWeight: 700, textTransform: "uppercase", letterSpacing: 0.5 }}
              >
                {group.title}
              </Typography>
              {group.key === "storeOpening" && snagListStatus === "Applicable" && (
                pointsClosed?.closed ? (
                  <Tooltip
                    title={`${pointsClosed.comment || ""}${
                      pointsClosed.closedAt
                        ? ` (${new Date(pointsClosed.closedAt).toLocaleDateString()})`
                        : ""
                    }`}
                  >
                    <Chip label="Points Closed" color="success" size="small" />
                  </Tooltip>
                ) : (
                  canMarkPointsClosed && (
                    <Button
                      size="small"
                      variant="outlined"
                      onClick={(e) => {
                        e.stopPropagation();
                        onMarkPointsClosed();
                      }}
                    >
                      Mark Points Closed
                    </Button>
                  )
                )
              )}
              {onEditStep &&
                group.editableSteps.map((stepNo) => (
                  <Button
                    key={stepNo}
                    size="small"
                    startIcon={<EditIcon fontSize="small" />}
                    onClick={(e) => {
                      e.stopPropagation();
                      onEditStep(stepNo);
                    }}
                  >
                    {group.editableSteps.length > 1 ? `Edit Step ${stepNo}` : "Edit"}
                  </Button>
                ))}
            </Stack>
          </AccordionSummary>
          <AccordionDetails sx={{ px: 0 }}>
            <Box
              sx={{
                display: "grid",
                gridTemplateColumns: "minmax(0, 1fr)",
                gap: 1,
                alignItems: "start",
              }}
            >
              {group.documents.map((doc) => (
                <DocumentCard
                  key={doc.key}
                  title={doc.title}
                  rows={doc.rows}
                  replaceableFields={replaceableFields}
                  onReplaceField={onReplaceField}
                  replacingFieldKey={replacingFieldKey}
                />
              ))}
            </Box>
          </AccordionDetails>
        </Accordion>
      ))}
    </>
  );
};

export default CfV2SubmittedInputs;
